import { useState, useMemo, useCallback } from "react";
import type { Deal } from "../../../../../../../models";
import { useGrowerAssignment } from "../shared";
import type { DealConfigurationProps } from "./DealConfiguration.types";

export const DEAL_PROPERTIES: { name: string; label: string; getValue: (d: Deal) => string }[] = [
	{ name: "season", label: "Season", getValue: (d) => d.season },
	{ name: "type", label: "Type", getValue: (d) => d.type },
	{ name: "density", label: "Density", getValue: (d) => `${d.density} acre` },
	{ name: "depth", label: "Depth", getValue: (d) => `${d.depth} in` },
	{ name: "testPackage", label: "Package", getValue: (d) => d.testPackage || "None" },
	{ name: "frequencyOfMicros", label: "Micros", getValue: (d) => `${d.frequencyOfMicros ?? 0}` },
	{ name: "labName", label: "Lab", getValue: (d) => d.labName || "" },
	{ name: "specialTag", label: "Tag", getValue: (d) => d.specialTag || "" },
	{
		name: "composite",
		label: "Composite",
		getValue: (d) => (d.isMultiComposite ? "Multi-Composite" : d.isComposite ? "Composite" : ""),
	},
];

export type DealSortOption =
	| "default"
	| "density-asc"
	| "density-desc"
	| "depth-asc"
	| "depth-desc"
	| "growers-desc"
	| "growers-asc";

export function useDealConfigurationViewModel(props: DealConfigurationProps) {
	const { jobs, allDeals, dealAssignments, onAssignGrowerToDeal, explodedGrowers, onExplodeGrower, onRegroupGrower } =
		props;

	const shared = useGrowerAssignment({
		jobs,
		entities: allDeals,
		assignments: dealAssignments,
		onAssign: onAssignGrowerToDeal,
		explodedGrowers,
		onExplodeGrower,
		onRegroupGrower,
	});

	// Hover state for cross-card chip highlighting
	const [hoveredChip, setHoveredChip] = useState<{ propertyName: string; value: string } | null>(null);

	// Deal sort, filter & search state
	const [dealSortOption, setDealSortOption] = useState<DealSortOption>("default");
	const [dealFilters, setDealFilters] = useState<Record<string, Set<string>>>({});
	const [searchText, setSearchText] = useState("");

	const setDealFilter = useCallback((propertyName: string, values: Set<string>) => {
		setDealFilters((prev) => ({ ...prev, [propertyName]: values }));
	}, []);

	const clearDealFilters = useCallback(() => {
		setDealFilters({});
	}, []);

	// Unique values per property for the filter popover
	const dealFilterOptions = useMemo((): Record<string, string[]> => {
		const result: Record<string, string[]> = {};
		for (const prop of DEAL_PROPERTIES) {
			const values = allDeals.map(prop.getValue).filter(Boolean);
			result[prop.name] = [...new Set(values)].sort();
		}
		return result;
	}, [allDeals]);

	// Apply filters, search, then sort
	const filteredSortedDeals = useMemo(() => {
		const trimmed = searchText.trim().toLowerCase();
		let deals = allDeals.filter((deal) => {
			for (const prop of DEAL_PROPERTIES) {
				const filterSet = dealFilters[prop.name];
				if (filterSet && filterSet.size > 0) {
					const value = prop.getValue(deal);
					if (!filterSet.has(value)) return false;
				}
			}
			if (trimmed) {
				const tokens = trimmed.split(/\s+/);
				const values = DEAL_PROPERTIES.map((prop) => prop.getValue(deal).toLowerCase());
				const allTokensMatch = tokens.every((token) => values.some((v) => v.includes(token)));
				if (!allTokensMatch) return false;
			}
			return true;
		});

		if (dealSortOption !== "default") {
			deals = [...deals].sort((a, b) => {
				switch (dealSortOption) {
					case "density-asc":
						return a.density - b.density;
					case "density-desc":
						return b.density - a.density;
					case "depth-asc":
						return a.depth - b.depth;
					case "depth-desc":
						return b.depth - a.depth;
					case "growers-desc": {
						const aCount = (shared.growersByEntity.get(a.id) || []).length;
						const bCount = (shared.growersByEntity.get(b.id) || []).length;
						return bCount - aCount;
					}
					case "growers-asc": {
						const aCount = (shared.growersByEntity.get(a.id) || []).length;
						const bCount = (shared.growersByEntity.get(b.id) || []).length;
						return aCount - bCount;
					}
					default:
						return 0;
				}
			});
		}

		return deals;
	}, [allDeals, dealFilters, dealSortOption, shared.growersByEntity, searchText]);

	const activeDealFilterCount = useMemo(
		() => Object.values(dealFilters).filter((s) => s.size > 0).length,
		[dealFilters],
	);

	return {
		...shared,
		hoveredChip,
		setHoveredChip,
		dealSortOption,
		setDealSortOption,
		dealFilters,
		setDealFilter,
		clearDealFilters,
		dealFilterOptions,
		filteredSortedDeals,
		activeDealFilterCount,
		searchText,
		setSearchText,
		totalDealCount: allDeals.length,
	};
}
